// Copyright ©️ 2026 eVolve MEP, LLC

import type { StrongOmit } from '@evolvemep/api-ts';

import { isNotNil } from 'helpers/isNotNil';

/** Wrapper around an autodesk property that guarantees the existence of a set of attributes and, optionally, display categories */
export type TypeSafeProperty<T extends string, C extends string | undefined = undefined> = StrongOmit<
  Autodesk.Viewing.Property,
  'attributeName' | 'displayCategory'
> & {
  attributeName: T;
  // displayCategory is optional. If it's not provided, just type it as some arbitrary string
  displayCategory: C extends string ? C : string;
};

/** Type-safe wrapper around Autodesk.Viewing.PropertyResult */
export type TypeSafePropertyResult<T extends string, C extends string | undefined = undefined> = StrongOmit<
  Autodesk.Viewing.PropertyResult,
  'properties'
> & {
  properties: TypeSafeProperty<T, C>[];
};

/**
 * Given a list of properties, finds the first matching property with the given attribute name (and optionally, displayCategory)
 */
export const findMatchingProperty = <T extends string, C extends string | undefined = undefined>(
  properties: TypeSafeProperty<T, C>[],
  attributeName: T,
  displayCategory?: C,
) =>
  properties.find(
    (p) =>
      p.attributeName === attributeName && (displayCategory === undefined || p.displayCategory === displayCategory),
  );

/**
 * Given a property result, return the first value of the property with the given attribute name (and optionally, displayCategory) and casts it to a string
 * (or undefined if it was not found or nullish)
 */
export const getObjectPropertyValueAsString = <T extends string, C extends string | undefined = undefined>(
  obj: TypeSafePropertyResult<T, C>,
  attributeName: T,
  displayCategory?: C,
) => {
  const value = findMatchingProperty(obj.properties, attributeName, displayCategory)?.displayValue;
  return isNotNil(value) ? String(value) : undefined;
};

/**
 * Given a property result, return the first value of the property with the given attribute name (and optionally, displayCategory) and casts it to a number
 * (or undefined if it was not found or nullish)
 **/
export const getObjectPropertyValueAsNumber = <T extends string, C extends string | undefined = undefined>(
  obj: TypeSafePropertyResult<T, C>,
  attributeName: T,
  displayCategory?: C,
) => {
  const value = findMatchingProperty(obj.properties, attributeName, displayCategory)?.displayValue;
  return isNotNil(value) ? Number(value) : undefined;
};
